/**
 * 웹 ↔ 앱 JS 브릿지. 앱 규약의 유일한 호출 지점.
 *
 * 규약 전문: docs/APP-BRIDGE.md
 *   - 1.2 호출 형식 (iOS는 객체, Android는 JSON 문자열)
 *   - 1.3 공통 호출 함수 (채널 존재 확인)
 *   - 1.7 앱 → 웹 (window.piyonnaHandler)
 *
 * 이 파일은 앱 규약만 안다. 무엇을 왜 보내는지는 호출하는 쪽이 안다.
 * 새 명령은 기존 래퍼 옆에 하나씩 붙인다.
 *
 * 반환값은 "보냈는가"일 뿐이다. 명령은 값을 돌려주지 않으므로(규약 1.1) 앱이 실제로 처리했는지는
 * 알 수 없다 — 채널은 있지만 그 명령을 모르는 구버전 앱은 조용히 무시한다(규약 1.5).
 */
(function () {
  'use strict';

  var CHANNEL = 'piyonna';

  // 3.4 openSimpleBrowser 의 표시 방식. 정의되지 않은 값은 앱이 호출 전체를 버린다(규약 1.5)
  var PRESENTATION_TYPES = ['push', 'modal'];

  // 3.10 · 3.13 OS 알림 권한 값. 세 값 밖은 오지 않는다
  var NOTIFICATION_PERMISSIONS = ['agree', 'disagree', 'undecided'];

  // 3.12 추적 동의 키. Shopify 이름(preferences · marketing)과 다르다 — 매핑은 Shopify 호출 지점이 한다
  var CONSENT_KEYS = ['analytics', 'personalization', 'advertising'];

  // 3.13 푸시 카테고리 ID — lowerCamelCase 식별자(영문 소문자로 시작, 영문 · 숫자만)
  var PUSH_CATEGORY = /^[a-z][a-zA-Z0-9]*$/;

  // 1.6 UA 앱 토큰 — `Piyonna/1.1.0 (iPhone17,3; iOS 26.0)`
  var IOS_APP_UA = /Piyonna\/\S+ \([^;)]*; iOS /;

  function iosChannel() {
    var handlers = window.webkit && window.webkit.messageHandlers;
    return (handlers && handlers[CHANNEL]) || null;
  }

  function androidChannel() {
    var aos = window[CHANNEL];
    return aos && typeof aos.postMessage === 'function' ? aos : null;
  }

  function isFilledString(value) {
    return typeof value === 'string' && value.trim() !== '';
  }

  function trimmed(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  // 생략(undefined · null)은 허용하고, 값이 있으면 String 이어야 한다.
  // 규약 1.5 의 타입 불일치를 여기서 먼저 걸러 「보냈는데 무시됐다」를 만들지 않는다.
  function isOptionalString(value) {
    return value === undefined || value === null || typeof value === 'string';
  }

  function isHttpUrl(value) {
    return isFilledString(value) && /^https?:\/\//i.test(value);
  }

  // 스킴을 포함한 절대 주소. 앞에 공백이 붙으면 앱이 형식 오류로 호출 전체를 버리므로(규약 1.5)
  // trim 하지 않고 원본 그대로 판정한다 — 여기서 먼저 걸러 「보냈는데 무시됐다」를 만들지 않는다.
  var ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:/i;

  function isAbsoluteUrl(value) {
    return typeof value === 'string' && ABSOLUTE_URL.test(value);
  }

  /**
   * 명령 전송. 인앱이 아니거나 보낼 수 없으면 false.
   * @param {string} name - 명령 이름
   * @param {Object} [parameters] - 파라미터 객체
   * @returns {boolean}
   */
  function call(name, parameters) {
    if (!name) return false;

    var payload = { name: name, parameters: parameters || {} };

    try {
      var ios = iosChannel();
      if (ios) {
        ios.postMessage(payload);
        return true;
      }

      var aos = androidChannel();
      if (aos) {
        aos.postMessage(JSON.stringify(payload));
        return true;
      }
    } catch (e) {
      // 브릿지 호출 실패가 지면을 깨뜨리면 안 된다
    }

    return false;
  }

  /**
   * 3.6 attAuthorized — iOS ATT 허용 여부. 앱이 문서의 어떤 스크립트보다 먼저 주입한다.
   *
   * ⚠️ window.piyonnaApp(앱이 주입) 은 window.PiyonnaApp(이 파일이 만드는 브릿지) 과 대문자 하나만
   * 다른 별개의 객체다. 소비자가 잘못 읽으면 조용히 undefined 가 된다.
   *
   * 이 파일은 defer 로 로드되므로 head 인라인(consent-signal 등)보다 늦게 실행된다. 그 자리에서는
   * 이 함수를 쓸 수 없고 주입값을 직접 읽어야 한다 — 규약 3.6 참고.
   * @returns {boolean|null} null 이면 판정 불가 — 일반 브라우저 · Android · 구버전 앱
   */
  function attAuthorized() {
    var injected = window.piyonnaApp;
    return injected && typeof injected.attAuthorized === 'boolean' ? injected.attAuthorized : null;
  }

  /**
   * 3.12 consent — 앱이 주입한 추적 동의. 세 필드가 모두 Boolean 일 때만 유효하다.
   * 원본을 내주지 않고 복사본을 돌려준다 — 소비자가 고치면 다른 소비자가 어긋난 값을 읽는다.
   * @returns {{analytics: boolean, personalization: boolean, advertising: boolean}|null}
   *   null 이면 인앱 아님 · 3.12 미지원 구버전 앱 → 기존 정책(쿠키 배너 + 3.6)으로 판정한다
   */
  function consent() {
    return normalizeConsent(window.piyonnaApp && window.piyonnaApp.consent);
  }

  function normalizeConsent(value) {
    if (!value || typeof value !== 'object') return null;

    var result = {};
    for (var i = 0; i < CONSENT_KEYS.length; i++) {
      var key = CONSENT_KEYS[i];
      if (typeof value[key] !== 'boolean') return null;
      result[key] = value[key];
    }
    return result;
  }

  /**
   * 3.13 notification — 앱이 주입한 OS 알림 권한 + 푸시 카테고리별 수신 여부.
   * pushSubscription 에 키가 없으면 미응답이다 — 토글은 OFF 로 그리되 false 와 구분된다(규약 3.13).
   * @returns {{permission: string, pushSubscription: Object<string, boolean>}|null}
   *   null 이면 인앱 아님 · 3.13 미지원 구버전 앱
   */
  function notification() {
    return normalizeNotification(window.piyonnaApp && window.piyonnaApp.notification);
  }

  function normalizeNotification(value) {
    if (!value || typeof value !== 'object') return null;
    if (NOTIFICATION_PERMISSIONS.indexOf(value.permission) === -1) return null;

    var source = value.pushSubscription;
    if (!source || typeof source !== 'object') return null;

    // Boolean 이 아닌 값은 미응답으로 본다 — 키를 빼서 「없음」으로 남긴다
    var subscription = {};
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key) && typeof source[key] === 'boolean') {
        subscription[key] = source[key];
      }
    }
    return { permission: value.permission, pushSubscription: subscription };
  }

  /**
   * iOS 앱 여부(규약 1.6 UA). 광고 판정(규약 3.12 — iOS 는 `advertising && attAuthorized === true`)과
   * ATT 미허용 안내(3.12 · 설계 4.4)의 플랫폼 분기에 쓴다. 브릿지 호출 가능 여부 판단에는 쓰지 않는다.
   * @returns {boolean}
   */
  function isIOSApp() {
    return IOS_APP_UA.test(navigator.userAgent || '');
  }

  /*
   * 앱 → 웹 통로는 전역 슬롯 하나뿐이다(규약 1.7). 구독자가 여럿이어도 서로 덮어쓰지 않도록
   * 등록은 이 파일에서 하고, 받은 값을 구독자에게 나눠 준다.
   *
   * ⚠️ 이 파일은 defer 라 head 인라인보다 늦게 실행된다. 그 사이 슬롯에 먼저 앉은 핸들러가 있으면
   * (consent-signal 의 onAttAuthorizedChange — GBConsent 의 ATT 계층) 덮어쓰지 않고 먼저 불러 준다.
   * 덮어쓰면 세션 중 ATT 변경이 GBConsent 에 닿지 않는다.
   */
  window.piyonnaHandler = window.piyonnaHandler || {};

  /**
   * @param {string} name - window.piyonnaHandler 의 슬롯 이름
   * @param {function(*): *} normalize - 앱이 넘긴 값 → 구독자에게 줄 값. undefined 면 통지를 버린다
   *   (규약 1.5 와 같은 태도 — 타입이 어긋난 값은 일부만 쓰지 않는다)
   * @returns {function(function): function(): void} 구독 함수 — 구독 해제 함수를 돌려준다
   */
  function relay(name, normalize) {
    var listeners = [];
    var previous = window.piyonnaHandler[name];

    window.piyonnaHandler[name] = function (value) {
      if (typeof previous === 'function') {
        try {
          previous(value);
        } catch (e) {
          // 먼저 앉아 있던 핸들러의 예외가 구독자 통지를 막으면 안 된다
        }
      }

      var normalized = normalize(value);
      if (normalized === undefined) return;

      // 통지 도중 구독 해제가 일어나도 순회가 어긋나지 않게 복사본을 돈다
      var snapshot = listeners.slice();
      for (var i = 0; i < snapshot.length; i++) {
        try {
          snapshot[i](normalized);
        } catch (e) {
          // 한 구독자의 예외가 나머지 통지를 막으면 안 된다
        }
      }
    };

    return function subscribe(listener) {
      if (typeof listener !== 'function') return function () {};

      listeners.push(listener);

      return function () {
        var index = listeners.indexOf(listener);
        if (index !== -1) listeners.splice(index, 1);
      };
    };
  }

  // 3.6 — Boolean 그대로 온다(앱 1.0.0 부터). 예전 문서의 { attAuthorized } 객체 모양도 받아 둔다
  var subscribeAttAuthorized = relay('onAttAuthorizedChange', function (value) {
    var authorized = typeof value === 'boolean' ? value : value && value.attAuthorized;
    return typeof authorized === 'boolean' ? authorized : undefined;
  });

  // 3.10 — 파라미터 객체가 아니라 문자열 하나다. 물어본 지면에만 온다
  var subscribeAppNotificationStatus = relay('onAppNotificationStatus', function (status) {
    return NOTIFICATION_PERMISSIONS.indexOf(status) !== -1 ? status : undefined;
  });

  // 3.12 — 바뀐 뒤의 전체 값. 모든 지면에 온다
  var subscribeConsent = relay('onConsentChange', function (parameters) {
    return normalizeConsent(parameters) || undefined;
  });

  // 3.13 — 바뀐 뒤의 권한 + 전체 카테고리 맵. 모든 지면에 온다
  var subscribeNotification = relay('onNotificationChange', function (parameters) {
    return normalizeNotification(parameters) || undefined;
  });

  window.PiyonnaApp = {
    /**
     * 브릿지 호출 가능 여부. UA가 아니라 채널 존재로 판단한다(규약 1.6).
     * @returns {boolean}
     */
    isAvailable: function () {
      return !!(iosChannel() || androidChannel());
    },

    /**
     * 명령 전송. 인앱이 아니거나 보낼 수 없으면 false.
     * @param {string} name - 명령 이름
     * @param {Object} [parameters] - 파라미터 객체
     * @returns {boolean}
     */
    call: function (name, parameters) {
      if (!name) return false;

      var payload = { name: name, parameters: parameters || {} };

      try {
        var ios = iosChannel();
        if (ios) {
          ios.postMessage(payload);
          return true;
        }

        var aos = androidChannel();
        if (aos) {
          aos.postMessage(JSON.stringify(payload));
          return true;
        }
      } catch (e) {
        // 브릿지 호출 실패가 지면을 깨뜨리면 안 된다 — 삼키되 기록은 남긴다.
        // GBTrack 은 이 스크립트보다 늦게 로드될 수 있으므로 존재 확인 후 호출한다.
        if (window.GBTrack && typeof window.GBTrack.logError === 'function') {
          window.GBTrack.logError('app-bridge:call:' + name, e);
        }
      }

      return false;
    },

    /**
     * 3.1 setTabBadge — 하단 탭 배지 수량 표시.
     * 앱은 잘못된 입력을 무시하지만(규약 1.5), 어긋난 값을 애초에 보내지 않는다.
     * @param {string} pageId - 탭 지면 식별자 (서버 app_launch_config.tab_list[].page_id)
     * @param {number} count - 총 수량. 0이면 앱이 배지를 숨긴다
     * @returns {boolean}
     */
    setTabBadge: function (pageId, count) {
      if (!isFilledString(pageId)) return false;
      if (typeof count !== 'number' || !isFinite(count) || count % 1 !== 0 || count < 0) return false;

      return call('setTabBadge', { pageId: pageId, count: count });
    },

    /**
     * 3.2 close — 별도로 띄워진 화면 닫기.
     * 탭 루트 웹뷰에서는 앱이 무시하므로, 「닫기」가 성립하는 화면에서만 부른다.
     * @returns {boolean}
     */
    close: function () {
      return call('close');
    },

    /**
     * 3.3 setLanguage — 앱 노출 언어 지정. 값이 실제로 바뀌면 앱이 초기 설정부터 다시 실행된다.
     *
     * 지원 언어 목록은 여기 두지 않는다 — 서비스가 언어를 늘릴 때 이 파일이 빠지면 호출이 조용히
     * 막힌다. 지원하지 않는 코드는 앱이 무시하고 경고 로그를 남긴다(규약 3.3).
     * @param {string} language - ISO 639-1 소문자 (현재 'en' · 'fr')
     * @returns {boolean}
     */
    setLanguage: function (language) {
      if (!isFilledString(language)) return false;

      return call('setLanguage', { language: language });
    },

    /**
     * 3.4 openSimpleBrowser — 지면을 새 화면(심플 브라우저)으로 띄운다.
     * @param {string} pageUrl - http(s) 절대 주소. 스토어프론트 도메인으로 제한하지 않는다
     * @param {Object} [options]
     * @param {string} [options.title] - 상단 바 타이틀. 생략하면 지면 <title> 을 따라간다
     * @param {'push'|'modal'} [options.presentationType] - 표시 방식. 생략하면 앱 기본값 push
     * @returns {boolean}
     */
    openSimpleBrowser: function (pageUrl, options) {
      if (!isHttpUrl(pageUrl)) return false;

      var settings = options || {};
      var parameters = { pageUrl: pageUrl };

      if (settings.title !== undefined && settings.title !== null) {
        // title 이 String 이 아니면 앱은 호출 전체를 버린다(규약 3.4). 여기서도 같이 버려서
        // "타이틀만 빠진 채 열렸다"는 어긋난 결과를 만들지 않는다
        if (typeof settings.title !== 'string') return false;
        // 공백뿐인 값은 생략과 같다(규약 3.4). 빈 값은 필드를 생략한다(규약 1.4)
        if (settings.title.trim() !== '') parameters.title = settings.title;
      }

      if (settings.presentationType !== undefined && settings.presentationType !== null) {
        if (PRESENTATION_TYPES.indexOf(settings.presentationType) === -1) return false;
        parameters.presentationType = settings.presentationType;
      }

      return call('openSimpleBrowser', parameters);
    },

    /**
     * 3.5 selectTab — 하단 탭 전환 + 그 지면을 히스토리 없이 재로드. 떠 있는 화면은 모두 닫힌다.
     * @param {string} pageId - 이동할 탭의 지면 식별자
     * @param {string} [pageUrl] - 전환 후 로드할 http(s) 절대 주소. 생략하면 탭 루트
     * @returns {boolean}
     */
    selectTab: function (pageId, pageUrl) {
      if (!isFilledString(pageId)) return false;

      var parameters = { pageId: pageId };

      if (pageUrl !== undefined && pageUrl !== null && pageUrl !== '') {
        // 스토어프론트 도메인 판정은 앱이 한다(규약 3.5). 프리뷰·커스텀 도메인을 웹이 잘못 걸러내면
        // 이동 자체가 막히므로 여기서는 형식만 본다.
        if (!isHttpUrl(pageUrl)) return false;
        parameters.pageUrl = pageUrl;
      }

      return call('selectTab', parameters);
    },

    /**
     * 3.7 openExternalBrowser — 주소를 앱 밖으로 넘긴다.
     * http(s) 는 기기 기본 브라우저로, 그 밖의 스킴은 그 스킴을 처리하는 앱으로 간다.
     *
     * 앱 화면은 그대로 남는다 — 웹뷰를 닫거나 이동시키지 않는다. 앱 안에서 이어 갈 지면은
     * openSimpleBrowser(3.4)로 띄운다.
     *
     * 실패를 돌려받을 통로가 없으므로(규약 1.1) 열리지 않을 수 있는 딥링크는 호출하는 쪽이
     * 대체 경로(같은 대상의 http(s) 주소)를 함께 준비한다.
     * @param {string} url - 스킴을 포함한 절대 주소. 스킴도 도메인도 제한하지 않는다
     * @returns {boolean}
     */
    openExternalBrowser: function (url) {
      if (!isAbsoluteUrl(url)) return false;

      return call('openExternalBrowser', { url: url });
    },

    /**
     * 3.8 setCustomer — 페이지 로드 시점의 고객 스냅샷. 앱이 푸시(Klaviyo) 프로필을 그 고객으로
     * identify 하거나, 비로그인이면 익명으로 되돌린다.
     *
     * 로그아웃 전용 명령은 규약에 없다 — 스토어프론트는 로그아웃 시점을 잡을 수 없으므로
     * 「비로그인 스냅샷」이 곧 해제 신호다. 그래서 빈 customerId 를 버리지 않고 그대로 보낸다.
     *
     * @param {Object} [customer] - 스냅샷. 생략하면 비로그인
     * @param {string} [customer.customerId] - Shopify Customer ID. 큰 정수이므로 **String** (규약 1.4).
     *   생략 · null · 공백뿐이면 비로그인 스냅샷이다
     * @param {string} [customer.email] - Klaviyo 프로필 매칭 보조 키. 비로그인 스냅샷에서는 보내지 않는다
     *
     * language 는 받지 않는다 — 언어 통지는 setLanguage(3.3)로 일원화했다(2026-09-29 앱 유닛 요청).
     * 두 명령의 호출 타이밍에 따라 앱 상태가 어긋날 수 있기 때문이다. 넘겨도 싣지 않는다.
     * @returns {boolean}
     */
    setCustomer: function (customer) {
      var snapshot = customer || {};

      // 타입이 어긋나면 앱은 호출 전체를 버린다(규약 3.8). 여기서 빈 값으로 뭉개면 안 된다 —
      // Number 로 온 id 가 비로그인 스냅샷이 되어 로그인 고객의 프로필을 reset 시킨다
      if (!isOptionalString(snapshot.customerId)) return false;
      if (!isOptionalString(snapshot.email)) return false;

      var customerId = trimmed(snapshot.customerId);

      // 비로그인 스냅샷. email 은 앱이 쓰지 않으므로 필드를 싣지 않는다(규약 1.4)
      if (!customerId) return call('setCustomer', {});

      var parameters = { customerId: customerId };
      var email = trimmed(snapshot.email);

      // 공백뿐인 값은 생략과 같다(규약 3.8). 빈 값은 필드를 생략한다(규약 1.4)
      if (email) parameters.email = email;

      return call('setCustomer', parameters);
    },

    /**
     * 3.9 requestAppNotification — 앱 알림(푸시) 수신 동의 요청.
     * 미확정이면 시스템 팝업, 거부면 OS 알림 설정, 허용이면 아무것도 하지 않는다 — 웹은 상태를 몰라도 된다.
     * 결과는 돌려주지 않는다. 3.13 지원 앱은 바뀐 권한을 onNotificationChange 로 알려 준다.
     * @returns {boolean}
     */
    requestAppNotification: function () {
      return call('requestAppNotification');
    },

    /**
     * 3.10 checkAppNotification — 앱 알림 수신 상태 질의. 답은 onAppNotificationStatus 로
     * **물어본 지면에만** 온다. 부르기 전에 구독해 둔다 — 구독자가 없으면 답은 조용히 사라진다.
     *
     * 3.13 지원 앱은 notification() 주입값 + onNotificationChange 로 충분해 이 명령이 필요 없다.
     * @returns {boolean}
     */
    checkAppNotification: function () {
      return call('checkAppNotification');
    },

    /**
     * 3.11 openNotificationSettings — OS 의 이 앱 알림 설정 화면을 연다. 상태를 따지지 않는다.
     * 미확정 상태여도 시스템 팝업은 뜨지 않는다 — 동의를 받는 게 목적이면 requestAppNotification.
     * @returns {boolean}
     */
    openNotificationSettings: function () {
      return call('openNotificationSettings');
    },

    /**
     * 3.12 setConsent — 추적 동의 변경 요청. **바뀐 키만** 보낸다(부분 갱신).
     * 전체 값을 보내면 다른 지면에서 바뀐 값을 낡은 값으로 덮어쓸 수 있다.
     *
     * 확정값은 onConsentChange 로 요청한 지면을 포함한 모든 지면에 온다. 유효한 값은 거부되지 않으므로
     * 토글은 응답을 기다리지 않고 바로 바꾼다(낙관적 갱신).
     * iOS 에서 advertising: true 면 앱이 이어서 ATT 팝업 · 설정 이동 Alert 를 띄운다 — 토글 값은 그대로다.
     *
     * @param {{analytics?: boolean, personalization?: boolean, advertising?: boolean}} changes
     *   하나 이상. 규약 키 밖의 필드는 싣지 않는다
     * @returns {boolean}
     */
    setConsent: function (changes) {
      if (!changes || typeof changes !== 'object') return false;

      var parameters = {};
      var count = 0;

      for (var i = 0; i < CONSENT_KEYS.length; i++) {
        var key = CONSENT_KEYS[i];
        var value = changes[key];
        if (value === undefined || value === null) continue;
        // Boolean 이 아닌 값이 하나라도 있으면 앱은 호출 전체를 버린다(규약 3.12). 여기서도 같이 버린다
        if (typeof value !== 'boolean') return false;
        parameters[key] = value;
        count++;
      }

      // 세 필드가 모두 없으면 앱이 호출 전체를 버린다(규약 3.12)
      if (count === 0) return false;

      return call('setConsent', parameters);
    },

    /**
     * 3.13 setPushSubscription — 푸시 카테고리별 수신 여부 변경. OS 알림 권한은 건드리지 않는다.
     * 확정값은 onNotificationChange 로 모든 지면에 온다. 토글은 낙관적으로 바로 바꾼다.
     *
     * 카테고리 목록은 여기 두지 않는다 — 앱도 형식만 본다(규약 3.13). 목록을 두면 카테고리를 늘릴 때
     * 이 파일이 빠져 호출이 조용히 막힌다.
     * @param {string} category - 카테고리 ID (현재 'marketing'). 앞뒤 공백은 앱이 제거하고 판정한다
     * @param {boolean} enabled - 수신 여부
     * @returns {boolean}
     */
    setPushSubscription: function (category, enabled) {
      if (!PUSH_CATEGORY.test(trimmed(category))) return false;
      if (typeof enabled !== 'boolean') return false;

      return call('setPushSubscription', {
        category: category.trim(),
        enabled: enabled,
      });
    },

    /**
     * 3.14 openAppSettings — OS 설정의 이 앱 화면을 연다. 상태를 따지지 않는 범용 명령.
     * 현재 용례는 iOS ATT 미허용 안내의 「설정에서 켜기」 버튼(규약 3.12).
     * 설정에서 바뀐 값은 onAttAuthorizedChange · onNotificationChange 로 온다.
     * @returns {boolean}
     */
    openAppSettings: function () {
      return call('openAppSettings');
    },

    attAuthorized: attAuthorized,

    consent: consent,

    notification: notification,

    isIOSApp: isIOSApp,

    /**
     * 3.6 onAttAuthorizedChange — ATT 허용 여부 변경 구독 (앱 → 웹, 규약 1.7).
     *
     * false → true 는 즉시 반영할 수 있지만, true → false 는 이미 로드된 태그를 회수할 수 없어
     * 다음 페이지 로드부터 반영된다(규약 3.6).
     * @param {function(boolean): void} listener
     * @returns {function(): void} 구독 해제
     */
    onAttAuthorizedChange: subscribeAttAuthorized,

    /**
     * 3.10 onAppNotificationStatus — checkAppNotification 응답 구독.
     * @param {function('agree'|'disagree'|'undecided'): void} listener
     * @returns {function(): void} 구독 해제
     */
    onAppNotificationStatus: subscribeAppNotificationStatus,

    /**
     * 3.12 onConsentChange — 추적 동의 변경 구독. 자기 요청의 확인과 다른 지면 · 네이티브 팝업에서
     * 바뀐 값이 모두 여기로 온다. 화면은 진입 때와 같은 렌더 함수로 처리한다.
     * @param {function({analytics: boolean, personalization: boolean, advertising: boolean}): void} listener
     * @returns {function(): void} 구독 해제
     */
    onConsentChange: subscribeConsent,

    /**
     * 3.13 onNotificationChange — OS 알림 권한 · 푸시 수신 여부 변경 구독.
     * 계기는 setPushSubscription 저장 · 앱 바텀시트 응답 · 시스템 팝업 응답 · OS 설정 변경 후 복귀.
     * @param {function({permission: string, pushSubscription: Object<string, boolean>}): void} listener
     * @returns {function(): void} 구독 해제
     */
    onNotificationChange: subscribeNotification,
  };
})();
